import { atom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';
import type { EntitySecurityContextEntry, FeatureName } from '../../../auth/Security';
import type { SidePanelDeclaration, SidePanelSession, SidePanelSubject } from '../../types';

export type SidePanelSurfaceRegistration = {
    ownerId: string;
    declarations: readonly SidePanelDeclaration[];
    subject: SidePanelSubject | null;
    entitySecurityChain: EntitySecurityContextEntry[];
    featureSecurityChain: FeatureName[];
    deepLinkBasePath: string;
    onVisiblePanelChange?: (panelId: string | undefined) => void;
};

export const surfaceAtom = atom(null as SidePanelSurfaceRegistration | null);
export const contributionsAtom = atom<ReadonlyMap<string, readonly SidePanelDeclaration[]>>(new Map());
export const sessionAtom = atom(null as SidePanelSession | null);
export const layoutAtom = atom({ isExpanded: false });

export type SidePanelStateSnapshot = {
    session: SidePanelSession | null;
    subject: SidePanelSubject | null;
    visiblePanelId: string | null;
    isExpanded: boolean;
    isOpen: (panelId: string, subject?: SidePanelSubject | null) => boolean;
    isDeclared: (panelId: string) => boolean;
};

export const surfaceOwnerIdAtom = atom((get) => get(surfaceAtom)?.ownerId);

const declaredPanelIdsAtom = selectAtom(
    atom((get) =>
        [
            ...new Set([
                ...(get(surfaceAtom)?.declarations.map(({ definition }) => definition.id) ?? []),
                ...[...get(contributionsAtom).values()].flatMap((declarations) =>
                    declarations.map(({ definition }) => definition.id)
                )
            ])
        ].sort()
    ),
    (panelIds) => panelIds,
    isEqual
);
const isDeclaredAtom = atom((get) => {
    const panelIds = new Set(get(declaredPanelIdsAtom));
    return (panelId: string) => panelIds.has(panelId);
});
const isOpenAtom = atom((get) => {
    const session = get(sessionAtom);
    return (panelId: string, subject?: SidePanelSubject | null) =>
        session?.panelId === panelId && (subject === undefined || session.subject === subject);
});
const isExpandedAtom = atom((get) => get(layoutAtom).isExpanded);

export const snapshotAtom = atom<SidePanelStateSnapshot>((get) => {
    const session = get(sessionAtom);
    return {
        session,
        subject: session?.subject ?? null,
        visiblePanelId: session?.panelId ?? null,
        isExpanded: get(isExpandedAtom),
        isOpen: get(isOpenAtom),
        isDeclared: get(isDeclaredAtom)
    };
});
